import { ChevronDown, ChevronRight, Maximize2, Minimize2, MoreHorizontal, Paperclip, SendHorizontal, Sparkles } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import { Avatar } from './avatar'
import { Chip } from './chip'
import { Menu, MenuContent, MenuItem, MenuTrigger } from './menu'
import { Section } from './section'
import { TraceLine, type TraceLineProps } from './trace-line'

export function RileyMark({ className }: { className?: string }) {
  return <Sparkles aria-hidden className={cn('size-5 fill-riley/15 text-riley', className)} />
}

export interface Citation {
  label: string
  kind?: 'desk' | 'conduit' | 'kb' | 'pbx' | 'unifi' | 'ninja' | 'cambium' | 'zabbix' | 'books' | 'other'
  internalOnly?: boolean
  selfWrite?: boolean
}

export function CitationChips({ citations }: { citations: Citation[] }) {
  return (
    <div className="mt-2 flex min-w-0 max-w-full flex-wrap gap-1.5">
      {citations.map((c) => (
        <Chip
          key={c.label}
          className="max-w-full min-w-0"
          tone={c.selfWrite ? 'warning' : 'outline'}
          title={
            c.internalOnly ? 'internal only: scrubbed before any contractor composer' : c.selfWrite ? 'Riley’s own write-back' : undefined
          }
        >
          <span className="min-w-0 truncate">{c.label}</span>
          {c.internalOnly ? <span className="text-2xs text-subtle">internal</span> : null}
        </Chip>
      ))}
    </div>
  )
}

export function RileyMessage({
  from,
  author,
  at,
  children,
  citations,
  hypothesis,
}: {
  /** Who wrote it. (Not `role`: that name belongs to ARIA.) */
  from: 'riley' | 'user'
  author?: string
  at?: string
  children: React.ReactNode
  citations?: Citation[]
  hypothesis?: boolean
}) {
  const riley = from === 'riley'
  return (
    <div className={cn('flex min-w-0 max-w-full gap-2.5', !riley && 'flex-row-reverse')} data-from={from}>
      {riley ? (
        <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-riley-soft">
          <RileyMark className="size-4" />
        </span>
      ) : (
        <Avatar name={author ?? 'You'} size="sm" className="mt-0.5" />
      )}
      <div className={cn('min-w-0 max-w-[88%]', !riley && 'text-right')}>
        <div
          className={cn(
            'inline-block min-w-0 max-w-full rounded-lg px-3 py-2 text-left text-base [overflow-wrap:anywhere] [&_pre]:max-w-full [&_pre]:overflow-x-auto',
            riley ? 'border border-riley-border/60 bg-riley-soft/60' : 'bg-muted',
          )}
        >
          {hypothesis ? (
            <Chip tone="riley" size="sm" className="mb-1">
              Hypothesis
            </Chip>
          ) : null}
          <div className="space-y-2">{children}</div>
          {citations?.length ? <CitationChips citations={citations} /> : null}
        </div>
        {at ? <p className="mt-0.5 text-2xs text-subtle">{at}</p> : null}
      </div>
    </div>
  )
}

export function RileyChatInput({
  placeholder = 'Ask Riley about this case…',
  suggestions,
  onSubmit,
  onAttach,
}: {
  placeholder?: string
  suggestions?: string[]
  onSubmit?: (text: string) => void
  /** Attach a file to the chat. The paperclip renders only when the caller can actually attach (no dead button). */
  onAttach?: () => void
}) {
  const [text, setText] = React.useState('')
  const textarea = React.useRef<HTMLTextAreaElement>(null)
  const resize = React.useCallback(() => {
    const node = textarea.current
    if (!node) return
    node.style.height = 'auto'
    const styles = getComputedStyle(node)
    const lineHeight = Number.parseFloat(styles.lineHeight) || 20
    const padding = (Number.parseFloat(styles.paddingTop) || 0) + (Number.parseFloat(styles.paddingBottom) || 0)
    const maxHeight = lineHeight * 8 + padding
    node.style.height = `${Math.min(node.scrollHeight, maxHeight)}px`
    node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [])

  React.useLayoutEffect(resize)

  const submit = () => {
    const t = text.trim()
    if (!t) return
    onSubmit?.(t)
    setText('')
  }
  return (
    <div className="space-y-2">
      <div className="flex min-w-0 items-end gap-2 rounded-lg border border-border-strong bg-card px-2.5 py-1.5 focus-within:border-riley">
        {onAttach ? (
          <button
            type="button"
            aria-label="Attach"
            title="Attach a file to the chat"
            onClick={onAttach}
            className="cursor-pointer text-subtle hover:text-foreground"
          >
            <Paperclip className="size-4" />
          </button>
        ) : null}
        <textarea
          ref={textarea}
          aria-label="Ask Riley"
          value={text}
          rows={1}
          wrap="soft"
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
          className="block min-h-7 min-w-0 flex-1 resize-none overflow-y-hidden whitespace-pre-wrap break-words bg-transparent py-1 text-sm leading-5 outline-none [overflow-wrap:anywhere] placeholder:text-subtle"
        />
        <button
          type="button"
          aria-label="Send to Riley"
          onClick={submit}
          className="cursor-pointer text-riley disabled:opacity-40"
          disabled={!text.trim()}
        >
          <SendHorizontal className="size-4" />
        </button>
      </div>
      {suggestions?.length ? (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSubmit?.(s)}
              className="cursor-pointer rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground hover:border-riley-border hover:text-riley"
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export type RileyMode = 'rail' | 'full'

export interface RileyRailProps {
  mode: RileyMode
  onModeChange: (mode: RileyMode) => void
  /** Structured case summary, pinned at top and never sourced from the legacy summary markdown. */
  summary?: React.ReactNode
  /** The summary card folded to its one-line header (patchbay#198). Uncontrolled (open) when omitted. */
  summaryCollapsed?: boolean
  onSummaryCollapsedChange?: (collapsed: boolean) => void
  context?: React.ReactNode
  trace?: TraceLineProps[]
  traceActions?: React.ReactNode
  /** Messages and confirm cards, oldest first. */
  chat: React.ReactNode
  input?: React.ReactNode
  /** Text by default; a surface may supply an interactive title (for example a queue Peek/Open button). */
  title?: React.ReactNode
  /** Cards waiting for a decision: a "2 pending" badge in the header that jumps to the first one (#417). */
  pending?: { count: number; onJump: () => void }
  /** The "..." menu (e.g. clear the thread, open full-size). Omitted or empty: no button at all. */
  options?: { label: string; onSelect: () => void; disabled?: boolean }[]
}

/**
 * Riley's rail (agent-first stacked sidebar, mockup 08): Context · Trace · Chat stacked. The
 * Maximize button (shift+r) opens Riley full-size: chat on the left, context + trace beside it.
 */
export function RileyRail({
  mode,
  onModeChange,
  summary,
  summaryCollapsed,
  onSummaryCollapsedChange,
  context,
  trace,
  traceActions,
  chat,
  input,
  title = 'Riley',
  pending,
  options,
}: RileyRailProps) {
  const full = mode === 'full'
  const [localFolded, setLocalFolded] = React.useState(false)
  const folded = summaryCollapsed ?? localFolded
  const setFolded = (next: boolean) => {
    setLocalFolded(next)
    onSummaryCollapsedChange?.(next)
  }
  const chatViewport = React.useRef<HTMLDivElement>(null)
  const chatContent = React.useRef<HTMLDivElement>(null)
  const pinned = React.useRef(true)
  const [hasNewContent, setHasNewContent] = React.useState(false)

  const isAtNewest = React.useCallback(() => {
    const node = chatViewport.current
    if (!node) return true
    return node.scrollHeight - node.scrollTop - node.clientHeight <= 32
  }, [])

  const scrollToNewest = React.useCallback((behavior: ScrollBehavior = 'auto') => {
    const node = chatViewport.current
    if (!node) return
    if (typeof node.scrollTo === 'function') node.scrollTo({ top: node.scrollHeight, behavior })
    else node.scrollTop = node.scrollHeight
    pinned.current = true
    setHasNewContent(false)
  }, [])

  // A mode/order change starts at the newest turn. Streaming changes are followed by the observer below.
  React.useLayoutEffect(() => scrollToNewest(), [scrollToNewest])

  React.useEffect(() => {
    const content = chatContent.current
    if (!content || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (pinned.current) scrollToNewest()
      else setHasNewContent(true)
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [scrollToNewest])
  const traceBlock = trace?.length ? (
    <Section title="Trace" collapsible defaultOpen={full} actions={traceActions} bodyClassName="py-1.5">
      <div className="divide-y divide-border">
        {trace.map((t) => (
          <TraceLine key={`${t.source}-${t.detail}`} {...t} />
        ))}
      </div>
    </Section>
  ) : null
  // Stacked rail: Context is capped so Trace and Chat stay on screen; full-size lets it grow.
  const contextBlock = context ? (
    <Section title="Context" collapsible defaultOpen bodyClassName={full ? 'py-1.5' : 'max-h-[34vh] overflow-y-auto py-1.5'}>
      {context}
    </Section>
  ) : null
  const chatBlock = (
    <section
      aria-label="Riley chat"
      className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xs"
    >
      {summary ? (
        <div
          className={cn('border-b border-border bg-riley-soft/50 px-3.5', folded ? 'py-1.5' : 'py-2.5')}
          data-collapsed={folded ? 'true' : undefined}
        >
          <button
            type="button"
            onClick={() => setFolded(!folded)}
            aria-expanded={!folded}
            title={folded ? 'Show Riley’s summary' : 'Collapse Riley’s summary'}
            className={cn(
              'flex w-full cursor-pointer items-center gap-1.5 text-left text-2xs font-semibold uppercase tracking-wide text-riley',
              !folded && 'mb-0.5',
            )}
          >
            <RileyMark className="size-3.5" /> Riley’s summary
            {folded ? <ChevronRight className="ml-auto size-3.5" /> : <ChevronDown className="ml-auto size-3.5" />}
          </button>
          {folded ? null : <div className="max-h-48 overflow-y-auto text-sm text-foreground/85">{summary}</div>}
        </div>
      ) : null}
      <div className="relative min-h-0 min-w-0 flex-1">
        <div
          ref={chatViewport}
          className="h-full min-w-0 max-w-full overflow-x-hidden overflow-y-auto px-3.5 py-3"
          data-slot="riley-chat-scroll"
          data-newest-edge="bottom"
          onScroll={() => {
            const atNewest = isAtNewest()
            pinned.current = atNewest
            if (atNewest) setHasNewContent(false)
          }}
        >
          <div ref={chatContent} className="min-w-0 max-w-full">
            {chat}
          </div>
        </div>
        {hasNewContent ? (
          <button
            type="button"
            aria-label="New messages"
            onClick={() => scrollToNewest()}
            className="absolute right-3 bottom-3 cursor-pointer rounded-full border border-riley-border bg-card px-2.5 py-1 text-xs font-medium text-riley shadow-md"
          >
            ↓ New
          </button>
        ) : null}
      </div>
      {input ? <div className="border-t border-border p-3">{input}</div> : null}
    </section>
  )

  return (
    <div className="flex h-full min-w-0 max-w-full flex-col overflow-x-hidden" data-riley-mode={mode}>
      <div className="flex h-12 shrink-0 items-center gap-2 px-4">
        <RileyMark />
        {typeof title === 'string' ? <h2 className="text-xl font-semibold">{title}</h2> : <div className="min-w-0 flex-1">{title}</div>}
        <div className="ml-auto flex items-center gap-1">
          {pending && pending.count > 0 ? (
            <button
              type="button"
              onClick={pending.onJump}
              aria-label={`${pending.count} pending: jump to the first`}
              title="Jump to the cards waiting for you"
              data-slot="riley-pending-badge"
              className="inline-flex h-6 cursor-pointer items-center rounded-full bg-primary px-2 text-2xs font-semibold text-primary-foreground tabular hover:opacity-90"
            >
              {pending.count} pending
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onModeChange(full ? 'rail' : 'full')}
            aria-label={full ? 'Riley as sidebar' : 'Open Riley full-size'}
            title={full ? 'Back to sidebar (Shift+R)' : 'Open Riley full-size (Shift+R)'}
            className="inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {full ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </button>
          {options?.length ? (
            <Menu>
              <MenuTrigger
                aria-label="Riley options"
                className="inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              >
                <MoreHorizontal className="size-4" />
              </MenuTrigger>
              <MenuContent>
                {options.map((o) => (
                  <MenuItem key={o.label} disabled={o.disabled} onClick={o.onSelect}>
                    {o.label}
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          ) : null}
        </div>
      </div>
      {full ? (
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)] gap-3 px-4 pb-4">
          <div className="flex min-h-0 flex-col">{chatBlock}</div>
          <div className="min-h-0 space-y-3 overflow-y-auto">
            {contextBlock}
            {traceBlock}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3">
          {contextBlock}
          {traceBlock}
          <div className="flex min-h-[340px] flex-1 flex-col">{chatBlock}</div>
        </div>
      )}
    </div>
  )
}
